import {
	ActionIcon,
	Box,
	Group,
	Skeleton,
	Stack,
	useMantineTheme,
} from '@mantine/core';

import {
	listCustomPropertiesQueryKey,
	useCreateCustomProperty,
	useDeleteCustomProperty,
	useListCustomProperties,
	useUpdateCustomProperty,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EmptyState } from '@repo/common/components';
import RichTooltip from '@repo/common/components/RichTooltip/RichTooltip';
import { EntityType } from '@repo/common/enums/entityType';
import { Button, Icon, Text } from '@repo/foundations';
import { isEmpty } from 'lib0/object';
import { capitalize, cloneDeep, noop, uniqBy } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { queryClient, useAuthUser } from '../../api';
import { useUpdateCatalogProperties } from '../../api/hooks/catalog';
import { CustomPropertiesSettingsController } from '../../features/CustomPropertiesSettings';
import { useFeatureFlags } from '../../utils/featureFlags/useFeatureFlags';
import MenuList from '../CatalogView/CustomizeColumnsPanel/MenuList';
import { useColumnDefs } from '../CatalogView/hooks/useColumnDefs';
import IntegrationLogo from '../IntegrationLogo';
import {
	closeAllModals,
	openDeleteConfirmModal,
	openModal,
} from '../ModalManager';
import { PropertyForm } from './CatalogDefaultsSettings.form';
import type { CustomPropertyCatalogProperty } from './TagOptionsButton';
import { TagOptionsButton } from './TagOptionsButton';
const CATALOG_TYPE = 'custom_property';

function CatalogDefaultsSettings() {
	const theme = useMantineTheme();
	const { user, workspace } = useAuthUser();
	const { customPropertiesUiUpdate } = useFeatureFlags();

	const { mutateAsync: updateCatalogProperties } = useUpdateCatalogProperties();
	const { catalog } = useColumnDefs({
		defaultColumns: [],
		catalogServerType: 'catalog',
		catalogType: CATALOG_TYPE,
	});

	const variables = {};
	const { data: customProperties, isLoading } =
		useListCustomProperties(variables);

	const queryKey = listCustomPropertiesQueryKey(variables);

	const { mutateAsync: deleteCustomProperty } = useDeleteCustomProperty();
	const { mutateAsync: updateCustomProperty } = useUpdateCustomProperty({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey,
			});
		},
	});
	const { mutateAsync: createCustomProperty } = useCreateCustomProperty({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey,
			});
		},
	});

	const propertiesToDisplay: CustomPropertyCatalogProperty[] = useMemo(() => {
		const customPropertyList =
			customProperties
				?.sort((a, b) => {
					const aIndex =
						catalog?.properties.find((p) => p.value === a.name)?.order ?? 0;
					const bIndex =
						catalog?.properties.find((p) => p.value === b.name)?.order ?? 0;
					return aIndex - bIndex;
				})
				.map((cp, index) => ({
					...cp,
					default: false,
					value: cp.name,
					hidden: false,
					order: index,
				})) || [];

		return customPropertyList;
	}, [catalog?.properties, customProperties]);

	const onColumnReorder = useCallback(
		async (column: string | number, _toIndex: number) => {
			if (!catalog) {
				return null;
			}

			const toIndex = _toIndex - 1;

			const draft = cloneDeep(propertiesToDisplay);

			// Reorder column by name.
			const [removed] = draft.splice(
				draft.findIndex((f) => f.value === column),
				1
			);

			draft.splice(toIndex, 0, removed);

			// Update order of all properties.
			draft.forEach((draftProperty, index) => {
				draftProperty.order = index;
			});

			const result = await updateCatalogProperties({
				...catalog,
				properties: uniqBy(draft, (el) => el.value),
			});

			return result;
		},
		[catalog, propertiesToDisplay, updateCatalogProperties]
	);

	const handleColumnOrderChange = useCallback(
		async (columnName: string, toIndex: number) => {
			const reorderedCatalog = await onColumnReorder(columnName, toIndex + 1);

			if (reorderedCatalog) {
				trackEvent('catalog/customize/reorder', {}, user, workspace);
			}
		},
		[onColumnReorder, user, workspace]
	);

	const existingLabels = useMemo(
		() => propertiesToDisplay.map((column) => column.value),
		[propertiesToDisplay]
	);

	const getSubText = useCallback(
		(columnName: string): React.ReactNode => {
			const property = propertiesToDisplay?.find(
				(cp) => cp.value === columnName
			);
			if (property && 'entity_types' in property) {
				return (
					<Text size="xs" variant="text">
						{property.entity_types.includes(EntityType.all)
							? 'All'
							: property.entity_types
									?.map(capitalize)
									.map((type: string) => type.replace(/_/g, ' '))
									.join(', ')}
					</Text>
				);
			}
			return null;
		},
		[propertiesToDisplay]
	);

	const getLeftContent = useCallback(
		(columnName: string): React.ReactNode => {
			const property = propertiesToDisplay?.find(
				(cp) => cp.value === columnName
			);

			if (!property) {
				return (
					<Text className="label" size="sm">
						{columnName}
					</Text>
				);
			}

			// Check if property has integration information
			const propertyFromIntegration =
				'integration' in property && !!property.integration;

			if (propertyFromIntegration && property.integration) {
				const { integration } = property;
				return (
					<Group spacing="xs" noWrap>
						<Text className="label" size="sm">
							{property.value}
						</Text>
						<Box p="xs">
							<RichTooltip
								maxWidth={theme.other.space[60]}
								title={`Custom Property comes from ${integration.name}`}
								body={`This custom property is synced from ${integration.name}.`}
								linkLabel={`${integration.name} settings`}
								linkUrl={`/integrations/${integration.id}`}
							>
								<IntegrationLogo
									integrationType={integration.type}
									size={theme.other.iconSize.sm}
								/>
							</RichTooltip>
						</Box>
					</Group>
				);
			}

			return (
				<Text className="label" size="sm">
					{property.value}
				</Text>
			);
		},
		[propertiesToDisplay, theme.other.space, theme.other.iconSize]
	);

	const handleDeleteColumn = useCallback(
		(columnName: string) => {
			openDeleteConfirmModal({
				title: 'Delete Property?',
				description: "You can't undo this action.",
				confirmLabel: 'Delete Property',
				confirmProps: {
					variant: 'primary',
					tone: 'critical',
				},
				onConfirm: () => {
					deleteCustomProperty({
						pathParams: {
							customPropertyId:
								customProperties?.find((cp) => cp.name === columnName)?.id ||
								'',
						},
					})
						.then(() => {
							queryClient.invalidateQueries({
								queryKey,
							});
						})
						.catch((error) => {
							// eslint-disable-next-line no-console
							console.error('Failed to delete custom property:', error);
						});
				},
			});
		},
		[customProperties, deleteCustomProperty, queryKey]
	);

	if (customPropertiesUiUpdate) {
		return <CustomPropertiesSettingsController />;
	}

	return (
		<Stack>
			<Group>
				<Button
					onClick={() =>
						openModal({
							title: 'Create property',
							children: (
								<PropertyForm
									fields={['label', 'type', 'visibility']}
									onCancel={closeAllModals}
									onConfirm={(label, type, visibility) => {
										createCustomProperty({
											body: {
												name: label,
												value_type: type,
												entity_types: visibility,
											},
										});
										closeAllModals();
									}}
									existingLabels={existingLabels}
								/>
							),
						})
					}
				>
					Create property
				</Button>
			</Group>
			{isLoading && <Skeleton height={100} />}
			{!isLoading && isEmpty(propertiesToDisplay) && (
				<EmptyState
					iconName="tableColumn"
					title="No custom properties"
					description="Create a custom property to get started"
					includeGoBack={false}
					size="sm"
				/>
			)}

			{!isEmpty(propertiesToDisplay) && (
				<Box
					p={'xs'}
					sx={{
						backgroundColor: theme.other.getColor('surface/primary/default'),
						border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
						borderRadius: theme.radius.md,
					}}
				>
					<MenuList
						// eslint-disable-next-line react/no-unstable-nested-components
						getRightContent={(columnName) => (
							<Group className="custom-property-action-buttons" spacing="xs">
								<ActionIcon
									onClick={() => {
										const property = propertiesToDisplay.find(
											(p) => p.value === columnName
										) as CustomPropertyCatalogProperty;
										openModal({
											title: 'Edit Property',
											children: (
												<PropertyForm
													fields={
														!property?.default
															? ['label', 'type', 'visibility']
															: ['visibility']
													}
													onCancel={closeAllModals}
													onConfirm={(label, type, visibility) => {
														updateCustomProperty({
															pathParams: {
																customPropertyId:
																	customProperties?.find(
																		(cp) => cp.name === columnName
																	)?.id || '',
															},
															body: {
																name: label,
																value_type: type,
																entity_types: visibility,
															},
														});
														closeAllModals();
													}}
													existingLabels={existingLabels.filter(
														(label) => label !== columnName
													)}
													initialValues={{
														label: property?.value || '',
														type: property?.value_type || '',
														visibility:
															(property?.entity_types as EntityType[]) || [],
														integration: property?.integration || null,
													}}
												/>
											),
										});
									}}
									size="xs"
								>
									<Icon name="pencil" />
								</ActionIcon>
								<TagOptionsButton
									property={
										propertiesToDisplay.find(
											(p) => p.value === columnName
										) as CustomPropertyCatalogProperty
									}
									customPropertyId={
										customProperties?.find((cp) => cp.name === columnName)?.id
									}
								/>
								{!propertiesToDisplay.find((p) => p.value === columnName)
									?.default &&
									!propertiesToDisplay.find((p) => p.value === columnName)
										?.integration && (
										<ActionIcon
											onClick={() => handleDeleteColumn(columnName)}
											size="xs"
										>
											<Icon name="trash" />
										</ActionIcon>
									)}
							</Group>
						)}
						catalogProperties={propertiesToDisplay}
						onColumnOrderChange={handleColumnOrderChange}
						onVisibilityChange={noop}
						getSubText={getSubText}
						getLeftContent={getLeftContent}
					/>
				</Box>
			)}
		</Stack>
	);
}

export default CatalogDefaultsSettings;
